import { spawnSync } from "node:child_process";
import { writeFileSync, mkdirSync, readFileSync, existsSync, readdirSync } from "node:fs";
import { join } from "node:path";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { redactForFeed } from "@m8t-stack/api-contract";
import type { ArmSpec, ArmState, ExamType, ExamVerdict } from "@m8t-stack/api-contract";

/** Parse one arm token into an ArmState + optional profile.
 *  Syntax: <state>[#<profile>] where <state> is one of: off | live | pinned:<sha> | imported:<key>.
 *  The profile suffix is separated by '#', e.g. 'off#no-consolidate', 'live#consolidate'.
 *  Throws USAGE on anything malformed. */
function parseArmToken(tok: string, opts: { allowStub: boolean }): { state: ArmState; profile?: string } {
  // Split off an optional profile suffix at the LAST '#' — profile values don't contain '#'.
  const hashIdx = tok.indexOf("#");
  const statePart = hashIdx >= 0 ? tok.slice(0, hashIdx) : tok;
  const profile = hashIdx >= 0 ? tok.slice(hashIdx + 1) : undefined;

  if (statePart === "off" || statePart === "live") return { state: statePart, profile };
  if (statePart.startsWith("pinned:")) {
    const ref = statePart.slice("pinned:".length);
    if (ref.length === 0) {
      throw new LocalCliError({ code: "USAGE", message: `pinned arm needs a ref: 'pinned:<sha>' (got '${tok}')` });
    }
    return { state: { pinned: ref }, profile };
  }
  if (statePart.startsWith("imported:")) {
    const ref = statePart.slice("imported:".length);
    if (ref.length === 0) {
      throw new LocalCliError({ code: "USAGE", message: `imported arm needs a ref: 'imported:<key>' (got '${tok}')` });
    }
    if (!opts.allowStub) {
      throw new LocalCliError({
        code: "USAGE",
        message:
          `imported arms are stub-only (a one-file placeholder) — pass --allow-stub to run one anyway, ` +
          `or use live/off/pinned for a real scored run (got '${tok}')`,
      });
    }
    return { state: { imported: ref }, profile };
  }
  throw new LocalCliError({
    code: "USAGE",
    message: `unknown arm '${statePart}' — expected one of: off | live | pinned:<sha> | imported:<key> (got '${tok}')`,
  });
}

/** Parse the --arms spec grammar: arm ("+" arm)+ , >= 2 arms.
 *  Each arm token may carry an optional profile suffix: <state>#<profile>
 *  (e.g. 'off#no-consolidate+live#consolidate').
 *  Throws LocalCliError{code:"USAGE"} for any malformed input — ALWAYS pre-spawn. */
export function parseArmSpec(raw: string, brain: string, opts: { allowStub: boolean }): ArmSpec[] {
  const tokens = raw.split("+").map((t) => t.trim());
  if (tokens.length < 2 || tokens.some((t) => t.length === 0)) {
    throw new LocalCliError({
      code: "USAGE",
      message: `--arms needs >= 2 arms joined by '+' (e.g. 'live+off', 'pinned:a+pinned:b'); got '${raw}'`,
    });
  }
  return tokens.map((tok) => {
    const { state, profile } = parseArmToken(tok, opts);
    const arm: ArmSpec = { brain, state };
    if (profile !== undefined) arm.profile = profile;
    return arm;
  });
}

const EXAM_RUN_STATUSES: ReadonlySet<string> = new Set(["ok", "partial", "inconclusive_judge_error"]);
const EXAM_TYPES: ReadonlySet<string> = new Set([
  "impact", "dream_delta", "tournament",
  "continuity", "recall_pr", "forgetting", "skill_abstraction",
]);
const EXAM_VERDICT_VALUES: ReadonlySet<string> = new Set(["improved", "no_detectable_change", "regressed"]);

/** Parse the brain-exam orchestrator's --json stdout into a typed ExamVerdict.
 *  Fails closed like parseVerdict (skill.ts): $BRAIN_EXAM_BIN is operator-overridable,
 *  so the wire shape is validated at runtime — an unknown enum, a missing field, or a
 *  verdict that breaks the firewall biconditional is never trusted. */
export function parseExamVerdict(stdout: string): ExamVerdict {
  let raw: unknown;
  try {
    raw = JSON.parse(stdout);
  } catch (e) {
    throw new LocalCliError({
      code: "EXAM_BAD_OUTPUT",
      message: `brain-exam emitted non-JSON stdout: ${e instanceof Error ? e.message : String(e)}`,
    });
  }
  const v = raw as Partial<ExamVerdict> | null;
  if (
    v === null ||
    typeof v !== "object" ||
    typeof v.examType !== "string" ||
    !EXAM_TYPES.has(v.examType) ||
    typeof v.runStatus !== "string" ||
    !EXAM_RUN_STATUSES.has(v.runStatus) ||
    typeof v.taskSetVersion !== "string" ||
    typeof v.evalVersion !== "string" ||
    typeof v.powerNote !== "string" ||
    !Array.isArray(v.arms) ||
    !Array.isArray(v.suiteCritique) ||
    !(v.suiteCritique as unknown[]).every(
      (c) =>
        c !== null &&
        typeof c === "object" &&
        typeof (c as { taskId?: unknown }).taskId === "string" &&
        typeof (c as { text?: unknown }).text === "string",
    ) ||
    v.statTest === undefined ||
    typeof v.statTest !== "object" ||
    (v.verdict !== null && (typeof v.verdict !== "string" || !EXAM_VERDICT_VALUES.has(v.verdict)))
  ) {
    throw new LocalCliError({
      code: "EXAM_BAD_OUTPUT",
      message: "brain-exam emitted a malformed ExamVerdict (unknown enum or missing field) — refusing to trust it",
    });
  }
  // The firewall biconditional: verdict === null IFF runStatus is the hard-fail status.
  const isNull = v.verdict === null;
  const isHardFail = v.runStatus === "inconclusive_judge_error";
  if (isNull !== isHardFail) {
    throw new LocalCliError({
      code: "EXAM_BAD_OUTPUT",
      message:
        "brain-exam verdict violates the firewall: verdict must be null IFF runStatus === 'inconclusive_judge_error'",
    });
  }
  return v as ExamVerdict;
}

export const DEFAULT_REPS: Record<string, number> = { impact: 3, dream_delta: 5, skill_abstraction: 5 };

/** Infer the exam type from the arms when unambiguous; require --exam-type for >= 3 arms. */
export function inferExamType(arms: ArmSpec[], explicit: string | undefined): ExamType {
  if (explicit !== undefined) {
    if (!EXAM_TYPES.has(explicit)) {
      throw new LocalCliError({ code: "USAGE", message: `unknown --exam-type '${explicit}'` });
    }
    return explicit as ExamType;
  }
  if (arms.length === 2) {
    const [a, b] = arms;
    if (a.state === "live" && b.state === "off") return "impact";
    if (b.state === "live" && a.state === "off") return "impact";
    if (typeof a.state === "object" && "pinned" in a.state && typeof b.state === "object" && "pinned" in b.state) {
      return "dream_delta";
    }
  }
  throw new LocalCliError({
    code: "USAGE",
    message: "could not infer --exam-type from arms — pass --exam-type impact|dream_delta|tournament",
  });
}

/** skill_abstraction is explicit-only: it requires --skill <slug> AND both arms
 *  pinned (the base/merge shas from the skill-promotion event) — there is no inference path,
 *  mirroring the explicit-arms discipline for pinned exam types. Throws USAGE. */
export function assertAbstractionArmsExplicit(
  examType: ExamType,
  arms: ArmSpec[],
  skill: string | undefined,
): void {
  if (examType !== "skill_abstraction") return;
  if (typeof skill !== "string" || skill.length === 0) {
    throw new LocalCliError({
      code: "USAGE",
      message: "--exam-type skill_abstraction requires --skill <slug> (the promoted skill's slug)",
    });
  }
  const bothPinned = arms.length === 2 && arms.every((a) => typeof a.state === "object" && "pinned" in a.state);
  if (!bothPinned) {
    throw new LocalCliError({
      code: "USAGE",
      message:
        "--exam-type skill_abstraction requires exactly 2 explicit pinned arms " +
        "(the promotion event's base/merge shas), e.g. --arms 'pinned:<base>+pinned:<merge>'",
    });
  }
}

/** Resolve the DISTINCT judge deployment. */
export function resolveJudgeDeployment(
  flag: string | undefined,
  env: NodeJS.ProcessEnv,
): { deployment: string; warning?: string } {
  if (typeof flag === "string" && flag.length > 0) return { deployment: flag };
  const fromEnv = env.EXAM_JUDGE_DEPLOYMENT;
  if (typeof fromEnv === "string" && fromEnv.length > 0) return { deployment: fromEnv };
  return {
    deployment: "gpt-5-mini",
    warning:
      "no --deployment and no $EXAM_JUDGE_DEPLOYMENT — falling back to gpt-5-mini as the judge. " +
      "Name the DISTINCT judge deployment before any blessing/calibration run.",
  };
}

/** Brain-repo path fragments the --out report dir must never sit under (Law-1).
 *
 *  `-brain` is the load-bearing entry and is deliberately GENERIC: brain repos are named
 *  `<worker>-brain` by construction (see `brain create` and the install summary), so this
 *  catches every worker's brain in every customer org — including workers that do not
 *  exist yet.
 *
 *  It is generic because the per-name entries went stale in exactly the way you would
 *  expect. This list named `/azure-advisor-brain` and was never updated when that worker
 *  was renamed, so for the whole life of the previous name the guard did not know the
 *  live advisor's brain repo by name. Enumerating names means the guard silently loses
 *  coverage at each rename, which is the one moment it matters most.
 *
 *  Fails closed by design: an unrelated `--out` that happens to contain `-brain` is
 *  refused. For a Law-1 guard that is the correct direction to err. */
const BRAIN_REPO_MARKERS = ["m8t-labs/", "-brain", "exam-arm-"];
export function assertOutNotInBrainRepo(out: string): void {
  if (BRAIN_REPO_MARKERS.some((m) => out.includes(m))) {
    throw new LocalCliError({
      code: "USAGE",
      message: `--out '${out}' is under a brain repo — reports MUST live in the referee home (Law-1 guard)`,
    });
  }
}

/** Extract `slices.sealed` (the sealed task ids) from a manifest. The CLI reads ONLY this field.
 *  Tries JSON first (the test feeds JSON); else a minimal YAML scan for the `sealed:` list under `slices:`. */
export function parseSealedFromManifest(text: string): string[] {
  try {
    const j = JSON.parse(text) as { slices?: { sealed?: unknown } } | null;
    if (Array.isArray(j?.slices?.sealed)) return (j.slices.sealed as unknown[]).map(String);
  } catch {
    /* not JSON — fall through to the YAML scan */
  }
  // Minimal YAML: find `sealed:` (inline `[a, b]` or a block list of `- id` lines) within the `slices:` block.
  const inline = /sealed:\s*\[([^\]]*)\]/.exec(text);
  if (inline) {
    return inline[1].split(",").map((s) => s.trim().replace(/^["']|["']$/g, "")).filter(Boolean);
  }
  const lines = text.split("\n");
  const ids: string[] = [];
  let inSealed = false;
  for (const line of lines) {
    if (/^\s*sealed:\s*$/.test(line)) { inSealed = true; continue; }
    if (inSealed) {
      const m = /^\s*-\s*(.+?)\s*$/.exec(line);
      if (m) { ids.push(m[1].replace(/^["']|["']$/g, "")); continue; }
      if (/^\s*\S+:/.test(line)) break;              // next key ends the sealed list
    }
  }
  return ids;
}

/** Resolve the referee home EXPLICITLY (mirrors the Python runner's REFEREE_HOME resolution).
 *  Prefer $REFEREE_HOME; else, as a last resort, derive it from an --out path that sits under a
 *  `referee_home/` segment. Returns null when neither is available — the caller MUST then fail loud
 *  rather than emit an unredacted feed on uncertainty (the fail-open hazard fix). */
export function resolveRefereeHome(refereeHomeOut: string, env: NodeJS.ProcessEnv): string | null {
  const fromEnv = env.REFEREE_HOME;
  if (typeof fromEnv === "string" && fromEnv.length > 0) return fromEnv;
  // Fallback only: the --out path under a referee_home/ segment (the legacy derivation).
  const marker = "/referee_home/";
  const idx = refereeHomeOut.indexOf(marker);
  if (idx >= 0) return refereeHomeOut.slice(0, idx) + "/referee_home";
  return null;
}

/** Resolve a version TOKEN to its on-disk dir under <home>/tasksets/<worker>/.
 *  Mirrors the Python load_taskset resolver so BOTH runtimes agree on ONE canonical token: the
 *  on-disk dirs are worker-prefixed ('ezra-v1'), but the CLI default emits '<worker>@latest' and
 *  the golden fixture '<worker>@v1'. Order (first match wins): exact dir → '<worker>-<version>' →
 *  'latest' picks the newest '<worker>-*'. Returns the version dir NAME (not absolute) so the
 *  caller composes the manifest path; falls back to the raw token so the fail-loud read still
 *  throws on a genuinely missing manifest. */
export function resolveTaskSetDir(base: string, worker: string, version: string): string {
  if (existsSync(join(base, version))) return version;                 // (1) verbatim on-disk dir name
  const prefixed = `${worker}-${version}`;
  if (existsSync(join(base, prefixed))) return prefixed;               // (2) 'v1' -> '<worker>-v1'
  if (version === "latest" && existsSync(base)) {
    const dirs = readdirSync(base, { withFileTypes: true })
      .filter((d) => d.isDirectory() && d.name.startsWith(`${worker}-`))
      .map((d) => d.name)
      .sort();
    if (dirs.length > 0) return dirs[dirs.length - 1];                 // (3) newest worker-prefixed dir
  }
  return version;                                                      // nothing matched -> fail loud downstream
}

/** Read sealed task ids from the taskset manifest (slices.sealed) under the referee home.
 *  FAIL-LOUD: a sealed manifest is EXPECTED whenever a report is being written, so an unresolvable home
 *  or an unreadable manifest THROWS (EXAM_REDACTION_UNSAFE) — it never returns an empty set, which would
 *  silently emit an UNREDACTED feed (the Law-1 fail-open hazard). */
export function readSealedTaskIds(
  taskSetVersion: string,
  refereeHomeOut: string,
  env: NodeJS.ProcessEnv = process.env,
): Set<string> {
  // taskSetVersion is "<worker>@<version>" (or "<worker>@latest"); the manifest lives under the referee home.
  const [worker, version] = taskSetVersion.split("@");
  const home = resolveRefereeHome(refereeHomeOut, env);
  if (home === null) {
    throw new LocalCliError({
      code: "EXAM_REDACTION_UNSAFE",
      message:
        "cannot resolve the referee home to read the sealed-task manifest — set $REFEREE_HOME " +
        "(mirroring the Python runner) so the feed can be redacted; refusing to emit a possibly-unredacted feed (Law-1)",
    });
  }
  // Resolve the CLI/golden version TOKEN to the worker-prefixed on-disk dir (mirrors Python).
  const versionDir = resolveTaskSetDir(join(home, "tasksets", worker), worker, version);
  const manifestPath = join(home, "tasksets", worker, versionDir, "manifest.yaml");
  let text: string;
  try {
    text = readFileSync(manifestPath, "utf-8");
  } catch (e) {
    throw new LocalCliError({
      code: "EXAM_REDACTION_UNSAFE",
      message:
        `cannot read the sealed-task manifest at '${manifestPath}' (${e instanceof Error ? e.message : String(e)}) — ` +
        "refusing to emit a possibly-unredacted feed (Law-1); fix $REFEREE_HOME / the task-set path and re-run",
    });
  }
  return new Set(parseSealedFromManifest(text));        // the CLI reads only slices.sealed
}

export interface ExamPlan {
  worker: string;
  examType: ExamType;
  taskSetVersion: string;
  arms: ArmSpec[];
  reps: number;
  probes: string[] | null;
  pool: string;
  out?: string;
  keepArms: boolean;
  allowStub: boolean;
  judgeDeployment: string;
  observeWaitS: number;      // LA ingestion-lag wait before observe queries; 0 = instant
}

/** Pure plan-JSON assembly — the CLI↔orchestrator contract. */
export function buildPlan(args: {
  worker: string;
  examType: ExamType;
  taskSetVersion: string;
  arms: ArmSpec[];
  reps: number;
  probes: string[] | undefined;
  pool: string;
  out: string | undefined;
  keepArms: boolean;
  allowStub: boolean;
  judgeDeployment: string;
  observeWaitS: number;
}): ExamPlan {
  return {
    worker: args.worker,
    examType: args.examType,
    taskSetVersion: args.taskSetVersion,
    arms: args.arms,
    reps: args.reps,
    probes: args.probes ?? null,
    pool: args.pool,
    out: args.out,
    keepArms: args.keepArms,
    allowStub: args.allowStub,
    judgeDeployment: args.judgeDeployment,
    observeWaitS: args.observeWaitS,
  };
}

export class EvalExamCommand extends M8tCommand {
  static paths = [["eval", "exam"]];
  static usage = Command.Usage({
    description:
      "Run a brain exam: impact A/B + dream-delta. Resolves the plan, then shells " +
      "out to the Python `brain-exam` orchestrator (override its path with $BRAIN_EXAM_BIN). " +
      "Renders an ExamVerdict: three-valued verdict + power note + per-task flips.",
  });

  worker = Option.String();
  arms = Option.String("--arms");
  taskSet = Option.String("--task-set");
  examType = Option.String("--exam-type");
  skill = Option.String("--skill");
  reps = Option.String("-n,--reps");
  probes = Option.String("--probes");
  pool = Option.String("--pool");
  out = Option.String("--out");
  dryRun = Option.Boolean("--dry-run", false);
  keepArms = Option.Boolean("--keep-arms", false);
  allowStub = Option.Boolean("--allow-stub", false);
  deployment = Option.String("--deployment");
  output = Option.String("--output");
  observeWaitS = Option.String("--observe-wait-s");

  async executeCommand(): Promise<number> {
    // The orchestrator shells out synchronously (spawnSync), but this method MUST stay async:
    // the base class awaits it (M8tCommand.execute) and the unit tests assert on `.rejects`, both
    // of which depend on a thrown LocalCliError surfacing as a rejected promise rather than a
    // synchronous throw. This no-op await anchors that contract for require-await without changing behavior.
    await Promise.resolve();
    const worker = typeof this.worker === "string" ? this.worker : undefined;
    if (!worker) throw new LocalCliError({ code: "USAGE", message: "<worker> positional argument is required" });
    const armsRaw = typeof this.arms === "string" ? this.arms : undefined;
    if (!armsRaw) throw new LocalCliError({ code: "USAGE", message: "--arms <spec> is required" });

    // Parse arms (USAGE-failing pre-spawn, incl. imported-without-allow-stub).
    const arms = parseArmSpec(armsRaw, "git", { allowStub: this.allowStub === true });
    const examType = inferExamType(arms, typeof this.examType === "string" ? this.examType : undefined);
    assertAbstractionArmsExplicit(examType, arms, typeof this.skill === "string" ? this.skill : undefined);

    // Reps: explicit --reps, else per-exam-type default.
    let reps = DEFAULT_REPS[examType] ?? 3;
    if (typeof this.reps === "string") {
      const parsed = Number.parseInt(this.reps, 10);
      if (!Number.isInteger(parsed) || parsed < 1) {
        throw new LocalCliError({ code: "USAGE", message: `-n/--reps must be a positive integer (got '${this.reps}')` });
      }
      reps = parsed;
    }

    const taskSetVersion = typeof this.taskSet === "string" ? this.taskSet : `${worker}@latest`;
    const pool = typeof this.pool === "string" ? this.pool : "exam-worker";
    const probes =
      typeof this.probes === "string" ? this.probes.split(",").map((p) => p.trim()).filter(Boolean) : undefined;
    const out = typeof this.out === "string" ? this.out : undefined;
    if (out !== undefined) assertOutNotInBrainRepo(out);

    const { deployment, warning } = resolveJudgeDeployment(
      typeof this.deployment === "string" ? this.deployment : undefined,
      process.env,
    );
    if (warning) this.context.stderr.write(`⚠ ${warning}\n`);

    let observeWaitS = 0;
    if (typeof this.observeWaitS === "string") {
      const parsed = Number.parseInt(this.observeWaitS, 10);
      if (!Number.isInteger(parsed) || parsed < 0) {
        throw new LocalCliError({ code: "USAGE", message: `--observe-wait-s must be a non-negative integer (got '${this.observeWaitS}')` });
      }
      observeWaitS = parsed;
    }

    const plan = buildPlan({
      worker, examType, taskSetVersion, arms, reps, probes, pool, out,
      keepArms: this.keepArms === true, allowStub: this.allowStub === true, judgeDeployment: deployment,
      observeWaitS,
    });

    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);

    if (this.dryRun === true) {
      this.context.stdout.write(renderJson(plan) + "\n");
      this.context.stdout.write(
        `\n(dry-run) ~${String(arms.length)} arms x n=${String(reps)}; judge grading fan-out is the cost driver — ` +
        `confirm the run sits inside the ~150-550 judge-call band before driving workers.\n`,
      );
      return 0;
    }

    const bin = process.env.BRAIN_EXAM_BIN ?? "brain-exam";
    const res = spawnSync(bin, ["run", "--plan", "-", "--json"], { encoding: "utf-8", input: renderJson(plan) });
    if (res.error) {
      throw new LocalCliError({
        code: "EXAM_SPAWN",
        message:
          `could not run '${bin}' — set $BRAIN_EXAM_BIN to the brain-exam entrypoint ` +
          `(e.g. eval/.venv/bin/brain-exam): ${res.error.message}`,
      });
    }
    if (res.status !== 0) {
      throw new LocalCliError({ code: "EXAM_FAILED", message: (res.stderr || "brain-exam exited non-zero").trim() });
    }

    const verdict = parseExamVerdict(res.stdout);

    // Redaction write-out: full verdict.json + redacted feed.json under the referee home.
    if (out !== undefined) {
      const sealedTaskIds = taskSetVersion ? readSealedTaskIds(taskSetVersion, out) : new Set<string>();
      mkdirSync(out, { recursive: true });
      writeFileSync(join(out, "verdict.json"), JSON.stringify(verdict, null, 2));   // FULL (sealed runs included)
      const feed = redactForFeed(verdict, sealedTaskIds);                            // the ONLY feed producer
      writeFileSync(join(out, "feed.json"), JSON.stringify(feed, null, 2));          // REDACTED E1-facing feed
    }

    if (mode === "json") {
      this.context.stdout.write(renderJson(verdict) + "\n");
    } else {
      this.renderPretty(verdict);
    }
    // Exit-code policy: valid measurements return 0; only the hard-fail status is nonzero.
    return verdict.runStatus === "inconclusive_judge_error" ? 1 : 0;
  }

  private renderPretty(v: ExamVerdict): void {
    const w = (s: string) => this.context.stdout.write(s);
    const verdictLabel = v.verdict === null ? "INCONCLUSIVE" : v.verdict.toUpperCase();
    w(`${colors.field(verdictLabel)}  (${v.examType}, runStatus=${v.runStatus})  ${v.taskSetVersion}\n`);
    if (v.runStatus !== "ok") w(`  ${colors.error(`⚠ runStatus != ok — verdict informs but never graduates`)}\n`);
    w(`  ${v.powerNote}\n`);
    const cmp = v.comparisons?.[0];
    if (cmp) {
      w(`  flips: ${String(cmp.nGain)} gain / ${String(cmp.nLoss)} loss / d=${String(cmp.nDiscordant)}  p=${String(cmp.pValue)}\n`);
      for (const f of cmp.flips) {
        w(`    ${f.direction === "gain" ? "+" : "-"} ${f.taskId}${f.lowConfidence ? " (lowConfidence)" : ""}\n`);
      }
    }
    for (const c of v.suiteCritique) {
      const label = c.taskId ? `${c.taskId}: ${c.text}` : c.text;
      w(`  ${colors.dim("· " + label)}\n`);
    }
  }
}
